/**
 * AdvisorMount.tsx — wires live map state into the AdvisorPanel (T-501).
 *
 * Rendered INSIDE MapView (like TopTractsSidePanel) so `useTopTracts` resolves against the
 * map's tract source. It is the thin glue that turns MapShell state + the ranked in-view rows
 * into the `getContext()` the panel calls at send time; all the CONTEXT shaping (rounding,
 * null-tolerance, capping, tier words) lives in the tested pure `buildAdvisorContext`.
 *
 * `useTopTracts` is kept inert while the panel is closed (visible: open && showTracts); it
 * activates when the panel opens, so top-tracts are current by the time the user sends.
 *
 * Enriched (Sep 2026): lazy-fetches NearbyStats when the panel opens and a site score is
 * available, so the advisor context includes charger competition details (names, networks,
 * ports, distances). Uses the same fetchNearbyStats the PDF export uses — one extra
 * /api/ev-stations/nearby call, no change to scoring or map rendering.
 */
import { useEffect, useRef, useState } from 'react'
import { useTopTracts } from '../hooks/useTopTracts'
import { buildAdvisorContext } from '../lib/advisorContext'
import { AdvisorPanel } from './AdvisorPanel'
import { fetchNearbyStats } from '../lib/pdf/fetchNearby'
import type { NearbyStats } from '../lib/nearbyStats'
import type { CensusTract, SiteScoreResult } from '../lib/tractModel'
import type { MapTier } from '../lib/mapTiers'

export interface AdvisorMountProps {
  open: boolean
  onClose: () => void
  activeTract: CensusTract | null
  siteScore: SiteScoreResult | null
  activeLocation: { lat: number; lng: number } | null
  searchedAddress: string | null
  geocodeConfidence: 'exact' | 'approximate' | null
  tier: MapTier
  showTracts: boolean
}

export function AdvisorMount({
  open,
  onClose,
  activeTract,
  siteScore,
  activeLocation,
  searchedAddress,
  geocodeConfidence,
  tier,
  showTracts,
}: AdvisorMountProps) {
  const { rows } = useTopTracts({ tier, visible: open && showTracts, sortModeId: 'opportunity' })
  const topTracts = rows.map((r) => ({ geoid: r.geoid, rank: r.rank, lvi: r.props.lvi }))

  // ── Lazy-fetch NearbyStats when the panel opens with a site score ──────────
  // Mirrors the PDF export's fetchNearbyStats call (ExportPdfButton.tsx). The fetch fires once
  // per location change (keyed on lat/lng) and caches the result until the location changes.
  // If the fetch hasn't completed when the user sends, nearby is null — the advisor still works,
  // just without charger details.
  const [nearbyStats, setNearbyStats] = useState<NearbyStats | null>(null)
  const fetchKeyRef = useRef<string>('')

  useEffect(() => {
    if (!open || !activeLocation || !siteScore?.amenities?.nearbyPois) {
      return
    }
    const key = `${activeLocation.lat},${activeLocation.lng}`
    if (key === fetchKeyRef.current) return // already fetched for this location
    fetchKeyRef.current = key
    setNearbyStats(null) // clear stale data while fetching

    let cancelled = false
    fetchNearbyStats(
      activeLocation.lat,
      activeLocation.lng,
      siteScore.amenities.nearbyPois,
      siteScore.amenities.poiFetched ?? siteScore.amenities.nearbyPois.length,
    )
      .then((result) => {
        if (!cancelled) setNearbyStats(result)
      })
      .catch(() => {
        // Fetch failed — nearby stays null; advisor works without it.
      })
    return () => {
      cancelled = true
    }
  }, [open, activeLocation?.lat, activeLocation?.lng, siteScore])

  const getContext = () =>
    buildAdvisorContext({
      activeTract,
      siteScore,
      activeLocation,
      searchedAddress,
      geocodeConfidence,
      mapTier: tier,
      topTracts,
      nearby: nearbyStats,
    })

  return <AdvisorPanel open={open} onClose={onClose} getContext={getContext} />
}
